import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_CUSTOM_SANDBOX_SIZE_LIMITS } from '@runloop/reflex-contract';
import { type PublicConfig, type ServiceSettings } from '@reflex/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** Platform-admin read of deployment-wide Reflex settings. */
export function useServiceSettings() {
  return useQuery({
    queryKey: queryKeys.serviceSettings,
    queryFn: api.getServiceSettings,
  });
}

/**
 * Save deployment-wide settings and synchronize the public config cache so
 * organization custom-size forms use the new disk ceiling immediately.
 */
export function useUpdateServiceSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: ServiceSettings) => api.updateServiceSettings(settings),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.serviceSettings, settings);
      queryClient.setQueryData<PublicConfig>(queryKeys.config, (previous) =>
        previous
          ? {
              ...previous,
              customSandboxSizeLimits: {
                ...(previous.customSandboxSizeLimits ?? DEFAULT_CUSTOM_SANDBOX_SIZE_LIMITS),
                maxDiskSizeGb: settings.maxSandboxDiskSizeGb,
              },
            }
          : previous,
      );
    },
  });
}
